import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

// The anchor of the program section on the event page (EventDetail scrolls to it once the program is in).
export const PROGRAM_ANCHOR = 'programm';

// Whether the event has a public program the event page shows: the same rule as usePublicProgram
// (at least one public entry), without loading the people.
const useHasPublicProgram = (slug) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    if (!slug) return undefined;
    axios.get(`${API}/api/public/events/${encodeURIComponent(slug)}/program`)
      .then((r) => {
        if (!cancelled) setReady(Array.isArray(r.data?.entries) && r.data.entries.length > 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);
  return ready;
};

// How much of the viewport an image taller than the viewport has to fill to count as in view.
const FOCUS_SHARE = 0.9;

// True while the image is in view: completely on screen, or, when it is taller than the viewport,
// filling at least FOCUS_SHARE of it. It has to have loaded, before which it has no height. Checked
// on scroll, on resize and whenever the image changes size.
const useInView = (imgRef) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const check = () => {
      const img = imgRef.current;
      if (!img || !img.complete || !img.naturalHeight) return;
      const { top, bottom, height } = img.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const visible = Math.min(bottom, viewport) - Math.max(top, 0);
      setInView(height <= viewport
        ? top >= 0 && bottom <= viewport
        : visible >= viewport * FOCUS_SHARE);
    };
    const img = imgRef.current;
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    img?.addEventListener('load', check);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    if (observer && img) observer.observe(img);
    check();
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      img?.removeEventListener('load', check);
      observer?.disconnect();
    };
  }, [imgRef]);
  return inView;
};

const Wrapper = styled(Link)`
  position: relative;
  display: block;
  max-width: 100%;
  margin-bottom: 44px;
  border-radius: 2px;

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 4px;
  }
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  box-shadow: 0 10px 20px rgba(0,0,0,0.2);
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  ${Wrapper}:hover & {
    transform: scale(1.015);
    box-shadow: 0 14px 28px rgba(0,0,0,0.28);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    ${Wrapper}:hover & { transform: none; }
  }
`;

const PlainImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.2);
`;

// The label that says where the image leads, centred on the image's bottom edge (half on it, so it
// covers little of a poster). It slides in while the image is in view (useInView) and out again after.
const Label = styled.span`
  position: absolute;
  left: 50%;
  bottom: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #47ABDB;
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: bold;
  line-height: 1.2;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transform: translate(-50%, ${({ $shown }) => ($shown ? '50%' : 'calc(50% + 12px)')});
  transition: opacity 0.5s ease, transform 0.5s ease, background-color 0.25s ease;

  svg { transition: transform 0.25s ease; }

  ${Wrapper}:hover &, ${Wrapper}:focus-visible & {
    background: #0056b3;
  }
  ${Wrapper}:hover & svg, ${Wrapper}:focus-visible & svg {
    transform: translateX(3px);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 0.9rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: translate(-50%, 50%);
    transition: none;
    svg { transition: none; }
  }
`;

// An event card's image as a link to the event page, with a label on it that says so: "To the
// conference program" (to the program section) when the event has a public program, otherwise
// "To the event". Without a slug there is no event page: the plain image, no link.
const EventImageLink = ({ slug, src, alt }) => {
  const intl = useIntl();
  const ref = useRef(null);
  const hasProgram = useHasPublicProgram(slug);
  const shown = useInView(ref);

  if (!slug) return <PlainImage src={src} alt={alt} />;

  const label = hasProgram
    ? intl.formatMessage({ id: 'event.cta.program', defaultMessage: 'To the conference program' })
    : intl.formatMessage({ id: 'event.cta.event', defaultMessage: 'To the event' });

  return (
    <Wrapper to={hasProgram ? `/events/${slug}#${PROGRAM_ANCHOR}` : `/events/${slug}`}>
      <Image ref={ref} src={src} alt={alt} />
      <Label $shown={shown}>
        {label}
        <ArrowRight size={18} strokeWidth={2.5} aria-hidden="true" />
      </Label>
    </Wrapper>
  );
};

export default EventImageLink;
